import React, { useEffect, useState } from "react";
import { getInvoiceCatalog, getSellerInfo, createInvoice } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { SectionTitle, EmptyState } from "../components/UIKit";
import { CurrencyEur, Receipt, Star, ArrowRight, X, PaperPlaneTilt, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProductsPage() {
  const { selected, countries } = useCountry();
  const [catalog, setCatalog] = useState([]);
  const [leadTypes, setLeadTypes] = useState({});
  const [seller, setSeller] = useState(null);
  const [pickedProduct, setPickedProduct] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", address: "", vat_id: "", quantity: 1, lead_type: "installers", notes: "" });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const country = countries.find((c) => c.code === selected);

  useEffect(() => {
    getInvoiceCatalog().then((res) => {
      // Backwards compat: response can be either an array or {products, lead_types}
      if (Array.isArray(res)) {
        setCatalog(res);
        setLeadTypes({});
      } else {
        setCatalog(res.products || []);
        setLeadTypes(res.lead_types || {});
      }
    }).catch(() => {});
    getSellerInfo().then(setSeller).catch(() => {});
  }, []);

  const openForm = (p) => {
    setPickedProduct(p);
    const defaultLeadType = p.flexible_type === false ? (p.default_lead_type || "installers") : "installers";
    setForm({ name: "", company: "", email: "", address: "", vat_id: "", quantity: 1, lead_type: defaultLeadType, notes: "" });
    setResult(null);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setCreating(true);
    try {
      const res = await createInvoice({
        buyer_name: form.name.trim(),
        buyer_company: form.company.trim() || null,
        buyer_email: form.email.trim(),
        buyer_address: form.address.trim() || null,
        buyer_vat_id: form.vat_id.trim() || null,
        country_code: selected,
        lead_type: form.lead_type,
        items: [{ lookup_key: pickedProduct.lookup_key, quantity: parseInt(form.quantity, 10) || 1 }],
        notes: form.notes.trim() || null,
        send_email: true,
      });
      setResult(res);
      if (res.email_sent) {
        toast.success(`Invoice ${res.invoice.number} emailed to ${form.email}`);
      } else {
        toast.warning(`Invoice ${res.invoice.number} created but email failed: ${res.email_error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invoice creation failed");
    }
    setCreating(false);
  };

  const closeModal = () => { setPickedProduct(null); setResult(null); };

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline="MONETIZE · INVOICING" title="Products & Pricing" />

      {/* Setup warning if seller info incomplete */}
      {seller && !seller.address1 && (
        <div className="bg-[#FFF8E1] border border-[#F3D670] rounded-xl p-4 mb-6 text-sm flex items-start gap-2 shadow-sm" data-testid="seller-setup-warning">
          <Warning size={16} weight="bold" className="mt-0.5 flex-shrink-0 text-[#B47F00]" />
          <div>
            <b>Complete your seller details before sending invoices.</b> Set <code className="bg-white px-1 rounded">SELLER_ADDRESS_LINE1</code> and <code className="bg-white px-1 rounded">SELLER_ADDRESS_LINE2</code> in <code className="bg-white px-1 rounded">/app/backend/.env</code>. IBAN <code className="bg-white px-1 rounded">{seller.iban}</code> is set. Restart backend after editing.
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-white border border-[#0A0A0A]/10 rounded-2xl p-5 mb-8 shadow-sm" data-testid="invoicing-info">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#0A0A0A] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
          HOW IT WORKS
        </div>
        <div className="text-sm text-[#525252] leading-relaxed">
          Pick a product → fill in buyer details → we generate a professional PDF invoice and email it directly from <b>{seller?.email || "contact@solarbridge.eu"}</b> with SEPA payment instructions. Once the buyer wires the money, mark the invoice paid on the <b>Invoices</b> tab — the CSV lead list is auto-delivered to their inbox.
        </div>
        <div className="text-xs text-[#525252] leading-relaxed mt-3 pt-3 border-t border-[#0A0A0A]/5">
          <b className="text-[#0A0A0A]">Every project scale is included</b> — residential 10 kW rooftop installers, commercial &amp; industrial EPCs, and utility-scale MW+ developers. Filter by segment on delivery.
        </div>
      </div>

      {/* Products grid */}
      <div className="mb-3 text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
        PRODUCTS · {country?.flag} {country?.name?.toUpperCase()}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
        {catalog.map((p) => (
          <div
            key={p.lookup_key}
            data-testid={`product-${p.lookup_key}`}
            className={`${p.featured ? "border-2 border-[#3E8B3A] shadow-lg" : "border border-[#0A0A0A]/10 shadow-sm"} bg-white rounded-2xl p-6 relative hover:shadow-md transition-shadow duration-200`}
          >
            {p.featured && (
              <div className="absolute -top-3 left-5 bg-[#3E8B3A] text-[#0A0A0A] px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1 shadow-sm" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                <Star size={11} weight="fill" /> RECOMMENDED
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <Receipt size={16} weight="bold" className="text-[#0A0A0A]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                ONE-OFF
              </div>
            </div>
            <div className="text-xl tracking-tight leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
              {p.name}
            </div>
            <div className="text-sm text-[#525252] mt-1 min-h-[36px]">{p.detail}</div>
            <div className="mt-4 mb-4 flex items-baseline gap-1">
              <CurrencyEur size={20} weight="bold" className="text-[#0A0A0A]" />
              <span className="text-4xl tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>
                {(p.amount_cents / 100).toFixed(0)}
              </span>
            </div>
            <button
              data-testid={`request-invoice-${p.lookup_key}`}
              onClick={() => openForm(p)}
              className={`w-full ${p.featured ? "bg-[#3E8B3A] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"} px-4 py-3 text-xs tracking-[0.2em] uppercase rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 inline-flex items-center justify-center gap-1.5`}
              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            >
              REQUEST INVOICE <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      {catalog.length === 0 && <EmptyState title="Loading catalog…" />}

      {/* Modal */}
      {pickedProduct && (
        <div
          data-testid="invoice-modal-overlay"
          onClick={() => !creating && closeModal()}
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        >
          <div
            data-testid="invoice-modal"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-[#0A0A0A]/10 rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-[#0A0A0A]/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0A0A0A] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  REQUEST INVOICE · {country?.flag} {country?.name}
                </div>
                <div className="text-lg tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                  {pickedProduct.name}
                </div>
                <div className="text-sm text-[#525252]">€{(pickedProduct.amount_cents / 100).toFixed(0)} · {pickedProduct.detail}{form.lead_type ? ` · ${(leadTypes[form.lead_type]?.label || form.lead_type)}` : ""}</div>
              </div>
              <button data-testid="invoice-modal-close" onClick={closeModal} disabled={creating} className="text-[#525252] hover:text-black">
                <X size={20} weight="bold" />
              </button>
            </div>

            {result ? (
              <div className="p-6" data-testid="invoice-created-panel">
                <div className="text-2xl mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                  Invoice {result.invoice.number} created
                </div>
                <div className="text-sm text-[#525252] mb-4">
                  {result.email_sent
                    ? <>Emailed to <b>{result.invoice.buyer.email}</b> with a PDF attachment. Track its status on the <b>Invoices</b> tab.</>
                    : <>Invoice generated but email failed to send: <b className="text-red-700">{result.email_error}</b>. You can resend from the Invoices tab.</>}
                </div>
                <div className="border border-[#0A0A0A]/10 bg-[#EEF6EC] rounded-xl p-4 text-xs mb-4" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  <div>Amount: <b>€{(result.invoice.total_cents / 100).toFixed(2)}</b></div>
                  <div>Due: {result.invoice.due_date}</div>
                  <div>Reference: {result.invoice.number}</div>
                  <div>IBAN: {result.invoice.seller.iban}</div>
                </div>
                <div className="flex gap-2 justify-end">
                  <a
                    href={`/api/invoices/${result.invoice.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-[#0A0A0A]/20 bg-white px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase rounded-lg hover:bg-[#EEF6EC] hover:border-[#3E8B3A] transition-colors"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    DOWNLOAD PDF
                  </a>
                  <button
                    onClick={closeModal}
                    className="bg-[#3E8B3A] text-[#0A0A0A] px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    DONE
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 space-y-3">
                  <Field label="Type of leads" testId="invoice-input-lead-type">
                    {pickedProduct.flexible_type === false ? (
                      <div className="w-full border border-black/25 bg-[#F4F4F2] px-3 py-2 text-sm" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {(leadTypes[form.lead_type]?.label) || form.lead_type} <span className="text-[#525252]">· locked to this SKU</span>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(leadTypes).map(([key, meta]) => {
                            const active = form.lead_type === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                data-testid={`lead-type-${key}`}
                                onClick={() => setForm({ ...form, lead_type: key })}
                                className={`border-2 px-2 py-2 text-left text-xs transition-all duration-150 ${active ? "border-[#0047FF] bg-[#0047FF] text-white" : "border-black/20 bg-white text-[#0A0A0A] hover:border-black"}`}
                              >
                                <div className="tracking-[0.15em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>{meta.label}</div>
                                <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-[#525252]"}`}>{meta.hint}</div>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </Field>
                  <Field label="Buyer name *" testId="invoice-input-name">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invoice-input-name" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black" placeholder="Jonas Müller" />
                  </Field>
                  <Field label="Company" testId="invoice-input-company">
                    <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="invoice-input-company" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black" placeholder="BSW-Solar GmbH" />
                  </Field>
                  <Field label="Email *" testId="invoice-input-email">
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invoice-input-email" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black" placeholder="procurement@bsw-solar.de" />
                  </Field>
                  <Field label="Billing address (optional)" testId="invoice-input-address">
                    <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="invoice-input-address" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black resize-y" placeholder="Street 1&#10;12345 Berlin, Germany" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="VAT ID (optional)" testId="invoice-input-vat">
                      <input value={form.vat_id} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} data-testid="invoice-input-vat" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black" placeholder="DE123456789" />
                    </Field>
                    <Field label="Quantity" testId="invoice-input-quantity">
                      <input type="number" min={1} max={50} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="invoice-input-quantity" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black" />
                    </Field>
                  </div>
                  <Field label="Notes (visible on invoice)" testId="invoice-input-notes">
                    <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-input-notes" className="w-full border border-black/25 px-3 py-2 text-sm outline-none focus:border-black resize-y" placeholder="e.g. Delivery format: XLSX preferred" />
                  </Field>
                  <div className="text-xs text-[#525252] pt-2" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    Total: <b className="text-[#0A0A0A]">€{((pickedProduct.amount_cents / 100) * (parseInt(form.quantity, 10) || 1)).toFixed(2)}</b> · Payable to IBAN <b>{seller?.iban}</b> · Due in 14 days
                  </div>
                </div>
                <div className="p-5 border-t border-[#0A0A0A]/10 flex justify-end gap-2">
                  <button onClick={closeModal} disabled={creating} className="border border-[#0A0A0A]/20 bg-white px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase rounded-lg hover:bg-[#F4F4F2] transition-colors" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    CANCEL
                  </button>
                  <button
                    onClick={submit}
                    disabled={creating || !form.name.trim() || !form.email.trim()}
                    data-testid="invoice-submit"
                    className="bg-[#3E8B3A] text-[#0A0A0A] px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase rounded-lg shadow-md hover:shadow-lg hover:bg-[#347030] hover:-translate-y-0.5 disabled:bg-[#525252] disabled:text-white disabled:shadow-none disabled:translate-y-0 inline-flex items-center gap-1.5 transition-all duration-200"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    <PaperPlaneTilt size={12} weight="bold" /> {creating ? "GENERATING…" : "GENERATE & EMAIL INVOICE"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>{label}</div>
    {children}
  </div>
);
